import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { ScreenSkeleton } from '../components/atoms/ScreenSkeleton';
import { ScreenHeading } from '../components/molecules/ScreenHeading';
import { SectionHeading } from '../components/atoms/SectionHeading';
import Modal from '../components/molecules/Modal';
// Type for confirmation dialog state
type ConfirmDialogState = {
  open: boolean;
  customerName: string;
  siteName: string;
  assetName: string;
  assetTypeLabel: string; // e.g. 'Bucket', 'Machine', etc.
  onConfirm: () => void;
};
import { Label } from '../components/atoms/Label';
import { useAtom } from 'jotai';
import {
  createNewInspection,
  useAssetPage,
  useNewInspectionHelper,
} from '../store/newInspection/newInspectionAtoms';
import { SortingType } from '../components/atoms/SortingMenu';
import SearchWithSorting from '../components/molecules/SearchWithSorting';
import { AssetModel, EquipmentModel } from '../data/types';
import CollectionHelpers from '../helpers/collectionHelpers';
import InspectionInfoCard from '../components/atoms/InspectionInfoCard';
import { useLiveQueryCached } from '../hooks/useLiveQueryCached';
import { inspectionDB } from '../data/database.structure';
import StickyComponent from '../components/atoms/StickyComponent';
import { v4 } from 'uuid';
import { session } from '../services/auth/session';
import { useNavigate } from 'react-router';
import { Button } from '../components/atoms/Button';
import { routePaths } from '../routes/basePaths';
import { EquipmentType } from '../data/enums';
import DatabaseMineLocationMethods from '../data/database.minelocations.methods';
import DatabaseAssetsMethods from '../data/database.assets.methods';

const NewInspectionConstants = {
  ITEMS_TO_DISPLAY: 10000,
  Title: 'Select machine or bucket',
  MachineBucketTitle: 'Machine / bucket',
  BucketOnly: 'Bucket only',
};

export const NewInspection: FC = () => {
  const { state, setState } = useNewInspectionHelper();
  const { pagination, increment } = useAssetPage(1);
  const {
    pagination: searchPagination,
    increment: incrementSearchPage,
    reset: resetSearchPage,
  } = useAssetPage(1);

  const navigate = useNavigate();

  const [isLoadingMore, setLoadingMore] = useState(false); // use for loading more assets

  const [creatingInspection, setCreatingInspection] = useState(false); // use for processing creating inspection

  const [{ mutateAsync }] = useAtom(createNewInspection);

  // Confirmation dialog state
  const [confirmDialog, setConfirmDialog] = useState<ConfirmDialogState>({
    open: false,
    customerName: '',
    siteName: '',
    assetName: '',
    assetTypeLabel: '',
    onConfirm: () => {},
  });

  // Cached wrappers: plain useLiveQuery is undefined on every mount's first render,
  // so this screen skeletoned on every visit even with warm asset/location data.
  const assetModels = useLiveQueryCached('new-inspection-assets', async () => {
    const assets = await new DatabaseAssetsMethods(inspectionDB).getAssets({
      includeEquipmentParts: false,
    });

    assets.forEach((a) => {
      for (let index = 0; index < a.equipments.length; index++) {
        const e = a.equipments[index];
        if (
          e.equipmentCategory.toLowerCase() === EquipmentType.bucket &&
          e.name &&
          e.name.trim() !== ''
        ) {
          a.bucketName = e.name;
        }
      }
    });
    return assets;
  });

  const mineLocationsViewModels = useLiveQueryCached(
    'new-inspection-mine-locations',
    async () => {
      const mineLocationsMethods = new DatabaseMineLocationMethods(
        inspectionDB,
      );
      const mineLocations =
        await mineLocationsMethods.getMineLocationsWithData();
      return mineLocations;
    },
  );

  const handleLoadMore = useCallback(() => {
    if (
      window.innerHeight + document.documentElement.scrollTop !==
        document.documentElement.offsetHeight ||
      isLoadingMore
    ) {
      return;
    }
    setLoadingMore(true);
    if (state.searchValue && state.searchValue.trim() !== '') {
      //If search query is not empty, increment search pagination, else increment asset pagination
      incrementSearchPage();
    } else {
      increment();
    }
    setLoadingMore(false);
  }, [state.searchValue]);

  useEffect(() => {
    // handle load more on scroll
    window.addEventListener('scroll', handleLoadMore);
    return () => window.removeEventListener('scroll', handleLoadMore);
  }, [isLoadingMore]);

  const handleSortChange = (type: SortingType) => {
    setState((prev) => ({
      ...prev,
      sortConfig: {
        key: type,
        direction: undefined,
      },
      sortMenuOpen: false,
    }));
  };

  const buildSearchWithSorting = () => (
    <SearchWithSorting
      searchValue={state.searchValue}
      sortItems={[SortingType.alphabetical, SortingType.recent]}
      handleSortChange={handleSortChange}
      onChangedSearch={(searchValue) => {
        if (searchValue.trim().length) {
          resetSearchPage();
        }
        if (
          window.innerHeight + document.documentElement.scrollTop ===
          document.documentElement.offsetHeight
        ) {
          window.scrollTo(0, 0);
        }
        setState((prev) => ({ ...prev, searchValue }));
      }}
      sortConfig={state.sortConfig}
    />
  );

  const searchAssets = (
    assets: AssetModel[],
    searchQuery: string,
    take: number,
    sortTake: number,
  ) => {
    if (searchQuery.length) {
      return assets
        .filter(
          (a) =>
            a.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
            a.bucketName?.toLowerCase().includes(searchQuery.toLowerCase()) ||
            a.model.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
            a.siteName.toLowerCase().includes(searchQuery.toLowerCase()) ||
            a.model.assetType
              .toLowerCase()
              .includes(searchQuery.toLowerCase()) ||
            a.customerName.toLowerCase().includes(searchQuery.toLowerCase()),
        )
        .slice(0, sortTake);
    }
    return assets.slice(0, take);
  };

  // Actual inspection creation logic
  const doCreateInspection = async ({
    assetId,
    mineLocationId,
    equipmentId,
    siteId,
  }: {
    assetId?: number;
    mineLocationId?: number;
    equipmentId?: string;
    siteId: number;
  }) => {
    if (!creatingInspection) {
      setCreatingInspection(true);
    }
    try {
      const inspectionId = v4();
      const inspectorName = session.claims()?.name;
      if (!inspectorName) {
        throw new Error('No inspector name in the stored sign-in');
      }

      await mutateAsync({
        inspectionId: inspectionId,
        inspectorName,
        assetId: assetId,
        mineLocationId: mineLocationId,
        equipmentExternalId: equipmentId,
        siteId: siteId,
      });
      // TODO: need to set current inspection
      navigate(`/inspections/${inspectionId}`, { replace: true });
    } catch (e) {
      console.log(`created error: ${e}`);
      alert('Creating Inspection failed');
    } finally {
      setCreatingInspection(false);
    }
  };

  // Show confirmation dialog before creating inspection
  const confirmCreateInspection = ({
    assetName,
    customerName,
    siteName,
    assetTypeLabel,
    onConfirm,
  }: {
    assetName: string;
    customerName: string;
    siteName: string;
    assetTypeLabel: string;
    onConfirm: () => void;
  }) => {
    setConfirmDialog({
      open: true,
      assetName,
      customerName,
      siteName,
      assetTypeLabel,
      onConfirm,
    });
  };

  const bucketList: {
    equipment: EquipmentModel;
    siteId: number;
    siteName: string;
    customerId: number;
    customerName: string;
  }[] = useMemo(() => {
    const searchQuery = state.searchValue;

    const buckets: {
      equipment: EquipmentModel;
      siteName: string;
      customerName: string;
      customerId: number;
      siteId: number;
    }[] = [];

    mineLocationsViewModels?.forEach((location) => {
      location.equipments.forEach((e) => {
        if (
          // TODO: need to check if the equipment is bucket, not all Equipments have a category yet
          // e.equipmentCategory?.toLowerCase() === EquipmentType.bucket.toLowerCase() &&
          e.id > 0
        ) {
          buckets.push({
            equipment: e,
            siteName: location.siteName,
            customerName: location.customerName,
            siteId: location.siteId,
            customerId: location.customerId,
          });
        }
      });
    });

    assetModels?.forEach((a) => {
      a.equipments.forEach((e) => {
        if (
          e.equipmentCategory?.toLowerCase() ===
            EquipmentType.bucket.toLowerCase() &&
          e.id > 0 &&
          !buckets.some((b) => b.equipment.id === e.id)
        ) {
          buckets.push({
            equipment: e,
            siteName: a.siteName,
            customerName: a.customerName,
            siteId: a.siteId,
            customerId: a.customerId,
          });
        }
      });
    });

    return buckets.filter((e) =>
      searchQuery
        ? e.equipment.name.toLowerCase().includes(searchQuery.toLowerCase())
        : true,
    );
  }, [
    assetModels,
    mineLocationsViewModels,
    state.searchValue,
    state.sortConfig,
  ]);

  const assetList = useMemo(() => {
    const searchQuery = state.searchValue;
    const sortProvider = state.sortConfig.key;
    const assets = assetModels;
    if (sortProvider === SortingType.recent) {
      assets?.sort((a, b) => b.assetTypeId - a.assetTypeId); // sort by recent
    } else if (sortProvider === SortingType.alphabetical) {
      assets?.sort((a, b) => (a.name > b.name ? 1 : -1)); // sort by alphabe
    }
    const groupedByAssets = CollectionHelpers.groupBy(
      searchAssets(
        assets ?? [],
        searchQuery ?? '',
        pagination * NewInspectionConstants.ITEMS_TO_DISPLAY,
        searchPagination * NewInspectionConstants.ITEMS_TO_DISPLAY,
      ),
      (a) =>
        sortProvider === SortingType.alphabetical
          ? a.name.charAt(0).toUpperCase()
          : 'Recent',
    );
    const groupedAsset: Array<{ key: string; assets: AssetModel[] }> = [];

    groupedByAssets.forEach((a, key) => {
      groupedAsset.push({ key, assets: a });
    });

    return groupedAsset;
  }, [
    assetModels,
    pagination,
    searchPagination,
    state.searchValue,
    state.sortConfig,
  ]);

  const closeConfirmDialog = useCallback(() => {
    setConfirmDialog((prev) => ({ ...prev, open: false }));
  }, []);

  const buildAssetList = () =>
    assetList.length || bucketList.length ? (
      <div>
        <SectionHeading title={NewInspectionConstants.MachineBucketTitle} />
        <ul>
          {assetList.map((asset, _) => {
            return (
              <div
                className="items-center justify-center"
                key={asset.key + asset.key}
              >
                <Label
                  children={asset.key}
                  titleType="subHeader"
                  className="text-xl text-primaryColor"
                />
                <ul>
                  {asset.assets.map((a, _) => (
                    <InspectionInfoCard
                      name={`${a.name}${a.bucketName ? ` / ${a.bucketName}` : ''}`}
                      customerName={a.customerName}
                      model={a.model.name}
                      siteName={a.siteName}
                      useBucket={a.bucketName !== undefined}
                      type={a.model.assetType}
                      onPressed={() =>
                        confirmCreateInspection({
                          assetName: `${a.name}${a.bucketName ? ` / ${a.bucketName}` : ''}`,
                          customerName: a.customerName,
                          siteName: a.siteName,
                          assetTypeLabel: 'Machine',
                          onConfirm: () =>
                            doCreateInspection({
                              siteId: a.siteId,
                              assetId: a.id,
                            }),
                        })
                      }
                      key={a.id + a.siteId + a.name + a.model.assetType}
                    />
                  ))}
                </ul>
              </div>
            );
          })}
        </ul>
        {bucketList.length > 0 && (
          <section>
            <SectionHeading title={NewInspectionConstants.BucketOnly} />
            <ul>
              {bucketList.map((a, _) => (
                <InspectionInfoCard
                  name={a.equipment.name}
                  customerName={a.customerName}
                  useBucket={a.equipment.name !== undefined}
                  model={''}
                  siteName={a.siteName}
                  type={'Bucket'}
                  onPressed={() =>
                    confirmCreateInspection({
                      assetName: a.equipment.name,
                      customerName: a.customerName,
                      siteName: a.siteName,
                      assetTypeLabel: 'Bucket',
                      onConfirm: () =>
                        doCreateInspection({
                          siteId: a.siteId,
                          equipmentId: a.equipment.externalId,
                          assetId: a.equipment.assetId,
                          mineLocationId: a.equipment.mineLocationId,
                        }),
                    })
                  }
                  key={a.equipment.id + a.customerId + a.equipment.name}
                />
              ))}
            </ul>
          </section>
        )}
      </div>
    ) : (
      <div className="absolute top-1/2 left-1/2">
        <Label
          children="No results found"
          titleType="subHeader"
          className="font-bold text-black"
        />
      </div>
    );

  if (creatingInspection || !mineLocationsViewModels || !assetModels) {
    return <ScreenSkeleton fields={4} />;
  }

  return (
    <>
      <div className="w-full bg-white">
        <StickyComponent className="bg-white pt-4">
          <ScreenHeading title={NewInspectionConstants.Title} hideContext />

          <div>{buildSearchWithSorting()}</div>

          <div className="my-4">
            <Button
              fullWidth
              children="New bucket"
              type="button"
              variant="outline"
              onClick={() => navigate(routePaths.NewBucket)}
            />
          </div>
        </StickyComponent>
        <div className="mt-8">{buildAssetList()}</div>
      </div>
      {/* Confirmation Dialog */}
      {confirmDialog.open && (
        <Modal isOpen={confirmDialog.open} onClose={closeConfirmDialog}>
          <div className="rounded-lg bg-white p-6">
            <Label
              children={`Confirm New ${confirmDialog.assetTypeLabel} Inspection`}
              titleType="largeHeader"
            />
            <div className="my-4">
              <div className="mb-2">
                <span className="font-bold">Customer:</span>{' '}
                {confirmDialog.customerName}
              </div>
              <div className="mb-2">
                <span className="font-bold">Site:</span>{' '}
                {confirmDialog.siteName}
              </div>
              <div className="mb-2">
                <span className="font-bold">
                  {confirmDialog.assetTypeLabel}:
                </span>{' '}
                {confirmDialog.assetName}
              </div>
            </div>
            <div className="flex justify-end gap-4">
              <Button
                type="button"
                variant="outline"
                onClick={closeConfirmDialog}
              >
                Cancel
              </Button>
              <Button
                type="button"
                variant="primary"
                onClick={() => {
                  closeConfirmDialog();
                  confirmDialog.onConfirm();
                }}
              >
                Confirm
              </Button>
            </div>
          </div>
        </Modal>
      )}
    </>
  );
};
